import Navigation from "src/models/Navigation";
import FeaturedEventChooser from "src/services/featuredEventChooser";
import DiscoveryClient from "../clients/discoveryClient";
import { defaultHitsPerPage } from "../utils/pagination";

const MAX_RECOMMENDED_EVENTS = 6;

/*
  Event Search Wrapper
 */
export default class EventSearch {
	constructor({ context, pluginConfig }) {
		this.pluginConfig = pluginConfig;
		this.publisherSettings = pluginConfig.publisherSettings;
		this.frameConfig = pluginConfig.frameConfig;
		this.discoveryClient = new DiscoveryClient();

		const searchParams = {};

		// This means that the plugin is category specific. All queries will be scoped to this category.
		if (this.frameConfig.options.category_id) {
			searchParams.categoryId = this.frameConfig.options.category_id;

			// This is a bit tricky, when the plugin is category specific, the navigation categories become subcategories.
			if (context.query.category) {
				searchParams.subCategoryIds = new Navigation(pluginConfig).lookupBySlug(
					context.query.category,
				)?.id;
			}
		} else if (context.query.category) {
			searchParams.categoryId = new Navigation(pluginConfig).lookupBySlug(
				context.query.category,
			)?.id;
		}

		if (context.query.subcategory) {
			const subCategoriesList = context.query.subcategory.split("^");
			searchParams.subCategoryIds = subCategoriesList.map((subCategory) => {
				return subCategory.split("|")[0];
			});
		}

		if (context.query.towns) {
			searchParams.towns = context.query.towns;
		}

		if (context.query.countries) {
			searchParams.countries = context.query.countries;
		}

		if (context.query.searchTerm) {
			searchParams.searchTerm = context.query.searchTerm;
		} else if (context.query.query) {
			searchParams.searchTerm = context.query.query;
		}

		if (context.query.event) {
			searchParams.eventId = context.query.event.split("-")[0];
		}

		if (context.query.fromDate) {
			searchParams.fromDate = context.query.fromDate;
		}

		if (context.query.toDate) {
			searchParams.toDate = context.query.toDate;
		}

		if (context.query.virtual) {
			searchParams.virtual = context.query.virtual;
		}

		searchParams.multipleEventInstances = true;
		searchParams.publisher_id = this.publisherSettings.publisher.id;
		searchParams.hitsPerPage = defaultHitsPerPage();

		this.searchParams = searchParams;
	}

	// This function performs queries to Algolia to get the events, featured events and base facets.
	// It is now possible to start from a page other than 0, so we need to parse the page number
	async homePageEvents(page = 0) {
		const search = await this.discoveryClient.getHomePageEvents(
			this.publisherSettings.publisher.id,
			{
				...this.searchParams,
				page,
			},
		);

		const { rawEvents, rawFacets, rawFeaturedEvents, rawEditorsPickEvents } =
			search;

		return {
			rawEvents,
			rawFacets,
			rawFeaturedEvents,
			rawEditorsPickEvents,
		};
	}

	async moreEvents(page) {
		const search = await this.discoveryClient.getEvents({
			...this.searchParams,
			page,
		});

		return search.events;
	}

	async search({ searchTerm, page, hitsPerPage }) {
		const searchData = await this.discoveryClient.search({
			...this.searchParams,
			page: page || 0,
			searchTerm,
			hitsPerPage,
		});

		return {
			events: searchData.events,
			facets: searchData.facets,
			meta: {
				nbHits: searchData.meta?.nbHits,
			},
		};
	}

	async event() {
		const searchData = await this.discoveryClient.getEventById(
			this.searchParams.eventId,
			{
				publisher_id: this.publisherSettings.publisher.id,
			},
		);

		const { rawEvent, editorialContent, baseFacets, signedStaticMapUrl } =
			searchData;

		return {
			rawEvent: rawEvent,
			editorialContent: editorialContent,
			rawFacets: baseFacets,
			signedStaticMapUrl,
		};
	}

	async widgetEvents({ widgetNumber = 5, categoryId }) {
		// This endpoint is heavily cached, so this is a (naive) hack to get more events to choose from.
		// We'll then use the FeaturedEventChooser to pick the right amount of events.
		// We will improve this in the future.
		const filters = { ...this.searchParams, widgetNumber: 30 };

		if (categoryId) {
			filters.categoryId = categoryId;
		}

		const searchData = await this.discoveryClient.getWidgetEvents(
			this.publisherSettings.publisher.id,
			filters,
		);

		return new FeaturedEventChooser(
			searchData.rawEvents,
			widgetNumber,
		).choose();
	}

	async otherOccurrences(event) {
		const search = await this.discoveryClient.getEvents({
			hitsPerPage: 20,
			parentId: event.parentId,
			multipleEventInstances: true,
			notSourceId: event.data.source_id,
			publisher_id: this.publisherSettings.publisher.id,
		});

		return search.events;
	}

	async relatedFeatured(event) {
		const filters = {
			hitsPerPage: 50,
			publisher_id: this.publisherSettings.publisher.id,
		};

		if (event?.data) {
			filters.categoryId = event.data.main_category_path[0];
		}
		const search = await this.discoveryClient.getRecommendedEvents(filters);
		const excludeIds = event?.data?.objectID ? [event.data.objectID] : [];
		return new FeaturedEventChooser(
			search.events,
			MAX_RECOMMENDED_EVENTS,
			excludeIds,
		).choose();
	}

	async featuredEvents(page) {
		const search = await this.discoveryClient.getFeaturedEvents({
			...this.searchParams,
			page: page || 0,
		});

		return search.events;
	}

	async allEditorsPickEvents() {
		const search = await this.discoveryClient.getEditorialPickEvents(
			Object.assign({}, this.searchParams, { hitsPerPage: 100 }),
		);

		return search.events;
	}
}
